import { c as _c } from "react/compiler-runtime";
import { useField } from 'formik';
import { useContext, useEffect, useState } from 'react';
import useSWR from 'swr';
import { DiffEditor } from '@monaco-editor/react';
import styled, { useTheme } from 'styled-components';
import { ShortEventData } from '../../../../../../../../../typings/EventData.interface';
import { Request } from '../../../../../../../../../typings/Request.interface';
import { TransformationExecution } from '../../../../../../../../../typings/TransformationExecution.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import useSearchQuery from '../../../../../hooks/useSearchQuery';
import DisplayDate from '../../../../../common/DisplayDate';
import Dropdown from '../../../../../common/Dropdown';
import Editor from '../../../../../common/Editor';
import Badge from '../../../../../common/base/Badge';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Divider from '../../../../../common/base/Divider';
import Tabs from '../../../../../common/base/Tabs';
import Text from '../../../../../common/base/Text';
import { Div } from '../../../../../common/helpers/StyledUtils';
import EditorInput from '../../../../../common/Form/Fields/EditorInput';
import TextInput from '../../../../../common/Form/Fields/TextInput';
import { useToasts } from '../../../../../common/Toast';
import { binaryBodyHints } from '../../../../../../utils/binary-body-hint';
import { multipartFileHintOffsets } from '../../../../../../utils/multipart-file-hints';
const ApplyButtonWrapper = styled.div`
  visibility: hidden;
`;
const HoverableCardSection = styled(StyledCardSection)`
  &:hover ${ApplyButtonWrapper} {
    visibility: visible;
  }
`;

// File parts of a multipart body are described without their bytes, which
// transformations receive as `content` (a Buffer): hint at it next to them.
const fileContentHints = (text: string) => multipartFileHintOffsets(text).map(offset => ({
  offset,
  text: '  content: [Buffer]'
}));
const IOPanel: React.FC<{
  source_id: string | null;
  transformation_id: string | null;
  webhook_id: string | null;
  input_height_offset?: number;
}> = t0 => {
  const $ = _c(78);
  const {
    source_id,
    transformation_id,
    webhook_id
  } = t0;
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const active_tab = query.transform_tab || "input";
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const [selected_id, setSelectedId] = useState(null);
  const [t1,, t2] = useField("input");
  const {
    value: input
  } = t1;
  const {
    setValue: setInput
  } = t2;
  const [t3] = useField("output");
  const {
    value: output
  } = t3;
  const [,, t4] = useField("sample_event_data_id");
  const {
    setValue: setSampleEventDataId
  } = t4;
  const theme = useTheme();
  let t5;
  if ($[0] !== output || $[1] !== updateSearchQuery) {
    t5 = () => {
      if (output && Object.keys(output).length > 0) {
        updateSearchQuery({
          transform_tab: "output"
        });
      }
    };
    $[0] = output;
    $[1] = updateSearchQuery;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== output) {
    t6 = [output];
    $[3] = output;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  useEffect(t5, t6);
  let t7;
  if ($[5] !== source_id) {
    t7 = source_id && APIMethodKeys.requests.list({
      source_id,
      limit: 5
    });
    $[5] = source_id;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== HookdeckAPI.requests || $[8] !== source_id) {
    t8 = () => HookdeckAPI.requests.list({
      source_id,
      limit: 5
    });
    $[7] = HookdeckAPI.requests;
    $[8] = source_id;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const {
    data: requests
  } = useSWR(t7, t8);
  let t9;
  if ($[10] !== transformation_id) {
    t9 = transformation_id && APIMethodKeys.transformations.listExecutions(transformation_id, {
      limit: 5
    });
    $[10] = transformation_id;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== HookdeckAPI.transformations || $[13] !== transformation_id) {
    t10 = () => HookdeckAPI.transformations.listExecutions(transformation_id, {
      limit: 5
    });
    $[12] = HookdeckAPI.transformations;
    $[13] = transformation_id;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const {
    data: executions
  } = useSWR(t9, t10);
  const first_request = requests && requests.models[0] || executions && executions.models[0];
  let t11;
  if ($[15] !== first_request || $[16] !== webhook_id) {
    t11 = first_request && APIMethodKeys.requests.get(first_request.id, {
      webhook_id
    });
    $[15] = first_request;
    $[16] = webhook_id;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== HookdeckAPI.requests || $[19] !== first_request?.id || $[20] !== webhook_id) {
    t12 = () => HookdeckAPI.requests.get(first_request?.id as string, {
      webhook_id: webhook_id ?? undefined
    });
    $[18] = HookdeckAPI.requests;
    $[19] = first_request?.id;
    $[20] = webhook_id;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const {
    data: request
  } = useSWR(t11, t12);
  const first_execution = executions && executions.models[0];
  let t13;
  if ($[22] !== first_execution || $[23] !== transformation_id) {
    t13 = first_execution && APIMethodKeys.transformations.getExecution(transformation_id, first_execution.id);
    $[22] = first_execution;
    $[23] = transformation_id;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== HookdeckAPI.transformations || $[26] !== first_execution?.id || $[27] !== transformation_id) {
    t14 = () => HookdeckAPI.transformations.getExecution(transformation_id, first_execution?.id as string);
    $[25] = HookdeckAPI.transformations;
    $[26] = first_execution?.id;
    $[27] = transformation_id;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const {
    data: execution
  } = useSWR(t13, t14);
  const data = execution?.original_event_data || request?.data;
  const event_data_id = execution?.original_event_data_id || request?.original_event_data_id;
  let t15;
  if ($[29] !== setInput || $[30] !== setSampleEventDataId) {
    t15 = (id, data_0, event_data_id_0) => {
      setSelectedId(id);
      setSampleEventDataId(event_data_id_0 ?? null);
      setInput(JSON.stringify({
        headers: data_0.headers,
        body: data_0.body_format === "binary" && typeof data_0.body !== "object" ? null : data_0.body,
        query: data_0.query,
        parsed_query: data_0.parsed_query,
        path: data_0.path
      }, null, 2));
    };
    $[29] = setInput;
    $[30] = setSampleEventDataId;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const setData = t15;
  let t16;
  if ($[32] !== data || $[33] !== event_data_id || $[34] !== execution?.id || $[35] !== request?.id || $[36] !== selected_id || $[37] !== setData) {
    t16 = () => {
      if (data && !selected_id) {
        setData(execution?.id || request?.id, data, event_data_id);
      }
    };
    $[32] = data;
    $[33] = event_data_id;
    $[34] = execution?.id;
    $[35] = request?.id;
    $[36] = selected_id;
    $[37] = setData;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== data || $[40] !== selected_id) {
    t17 = [data, selected_id];
    $[39] = data;
    $[40] = selected_id;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  useEffect(t16, t17);
  let t18;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      direction: "column",
      grow: true
    };
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      x: 6,
      t: 3
    };
    t20 = {
      align: "center",
      justify: "space-between"
    };
    $[43] = t19;
    $[44] = t20;
  } else {
    t19 = $[43];
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = [{
      key: "input",
      label: "Input"
    }, {
      key: "output",
      label: "Output"
    }, {
      key: "diff",
      label: "Diff"
    }];
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== updateSearchQuery) {
    t22 = key => updateSearchQuery({
      transform_tab: key
    });
    $[46] = updateSearchQuery;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== active_tab || $[49] !== t22) {
    t23 = <Tabs tabs={t21} border={false} m={0} active_tab={active_tab} onTabSelected={t22} />;
    $[48] = active_tab;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== executions?.models || $[52] !== requests?.models || $[53] !== selected_id || $[54] !== setData || $[55] !== source_id || $[56] !== transformation_id || $[57] !== webhook_id) {
    t24 = source_id ? <ChangeDropdownRequest requests={requests?.models || []} webhook_id={webhook_id} selected_id={selected_id} setData={setData} /> : <ChangeDropdownExecutions executions={executions?.models || []} transformation_id={transformation_id} selected_id={selected_id} setData={setData} />;
    $[51] = executions?.models;
    $[52] = requests?.models;
    $[53] = selected_id;
    $[54] = setData;
    $[55] = source_id;
    $[56] = transformation_id;
    $[57] = webhook_id;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t23 || $[60] !== t24) {
    t25 = <Div p={t19} flex={t20}>{t23}{t24}</Div>;
    $[59] = t23;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Divider />;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== active_tab || $[64] !== output) {
    t27 = active_tab === "output" && (!output ? <Text p={3} muted={true}>Run your transformation to preview output.</Text> : <Editor flex={{
      direction: "column",
      grow: true
    }} prevent_theme_change={true} value={output} inline_hints={binaryBodyHints} />);
    $[63] = active_tab;
    $[64] = output;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== active_tab) {
    t28 = active_tab === "input" && <EditorInput flex={{
      direction: "column",
      grow: true
    }} height={undefined} key={active_tab} name="input" inline_hints={_temp} />;
    $[66] = active_tab;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== active_tab || $[69] !== input || $[70] !== output || $[71] !== theme) {
    t29 = active_tab === "diff" && (!output ? <Text p={3} muted={true}>Run your transformation to preview the diff.</Text> : <DiffEditor original={input} modified={output} theme={theme.mode === "dark" ? "dark" : "light"} onMount={(editor, monaco) => {
      if (theme.mode === "dark") {
        monaco.editor.defineTheme("dark", {
          base: "vs-dark",
          inherit: true,
          rules: [],
          colors: {
            "editor.background": theme.colors.surface.base.surface
          }
        });
        monaco.editor.setTheme("dark");
      }
    }} options={{
      enableSplitViewResizing: false,
      renderSideBySide: false,
      lineNumbers: "off",
      padding: {
        top: 8,
        bottom: 8
      },
      contextmenu: false,
      readOnly: true,
      domReadOnly: true,
      renderLineHighlightOnlyWhenFocus: true,
      hideCursorInOverviewRuler: true,
      overviewRulerBorder: false,
      guides: {
        indentation: false,
        highlightActiveIndentation: false
      },
      minimap: {
        enabled: false
      },
      scrollbar: {
        vertical: "hidden",
        horizontal: "hidden"
      },
      overviewRulerLanes: 0,
      matchBrackets: "never",
      scrollBeyondLastColumn: 0,
      scrollBeyondLastLine: true
    }} originalLanguage="json" modifiedLanguage="json" height="100vh" />);
    $[68] = active_tab;
    $[69] = input;
    $[70] = output;
    $[71] = theme;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== t25 || $[74] !== t27 || $[75] !== t28 || $[76] !== t29) {
    t30 = <Div flex={t18}>{t25}{t26}{t27}{t28}{t29}</Div>;
    $[73] = t25;
    $[74] = t27;
    $[75] = t28;
    $[76] = t29;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  return t30;
};
const ChangeDropdownRequest: React.FC<{
  requests: Request[];
  webhook_id: string | null;
  selected_id: string | null;
  setData: (id: string, data: ShortEventData, event_data_id?: string | null) => void;
}> = t0 => {
  const $ = _c(33);
  const {
    requests,
    webhook_id,
    selected_id,
    setData
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const [t1] = useField("request_id");
  const {
    value: request_id
  } = t1;
  let t2;
  if ($[0] !== HookdeckAPI.requests || $[1] !== addToast || $[2] !== setData || $[3] !== webhook_id) {
    t2 = id => HookdeckAPI.requests.get(id, {
      webhook_id: webhook_id ?? undefined
    }).then(request => {
      setData(id, request.data, request.original_event_data_id);
      addToast("success", `Request data applied for ID ${id}`);
    }).catch(() => {
      addToast("error", `Could not find data for ID ${id}`);
    });
    $[0] = HookdeckAPI.requests;
    $[1] = addToast;
    $[2] = setData;
    $[3] = webhook_id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleSelected = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 2
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      minWidth: "546px"
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Text semi={true} size="s" m={{
      t: 0,
      b: 1
    }}>Latest requests</Text>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== handleSelected || $[9] !== requests || $[10] !== selected_id) {
    let t7;
    if ($[12] !== handleSelected || $[13] !== selected_id) {
      t7 = request_0 => <HoverableCardSection key={request_0.id} flex={{
        align: "center",
        gap: 3,
        justify: "space-between"
      }} p={3}><Div flex={{
          align: "center",
          gap: 3
        }}><Badge danger={!!request_0.rejection_cause} success={!request_0.rejection_cause} subtle={true}>{request_0.rejection_cause ? "Rejected" : "Accepted"}</Badge><Text mono={true} muted={true} flex={{
            align: "center",
            gap: 1
          }}><DisplayDate key={request_0.ingested_at} date={request_0.ingested_at} /><span>·</span>{request_0.id}</Text></Div><ApplyButtonWrapper><Button ghost={true} disabled={selected_id === request_0.id} m={{
            y: -4,
            r: -1.5
          }} onClick={() => handleSelected(request_0.id)}>{selected_id === request_0.id ? "Applied" : "Apply"}</Button></ApplyButtonWrapper></HoverableCardSection>;
      $[12] = handleSelected;
      $[13] = selected_id;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    t6 = requests.map(t7);
    $[8] = handleSelected;
    $[9] = requests;
    $[10] = selected_id;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[15] !== requests.length) {
    t7 = requests.length === 0 && <Text p={3} muted={true} center={true}>No requests found</Text>;
    $[15] = requests.length;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <StyledCard>{t6}{t7}</StyledCard>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Text semi={true} size="s" m={{
      t: 8,
      b: 1
    }}>Specific request ID</Text>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "center",
      gap: 2
    };
    t11 = {
      y: 2
    };
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TextInput w={100} m={{
      b: 0
    }} name="request_id" placeholder="Request ID" />;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = request_id === selected_id;
  let t14;
  if ($[24] !== handleSelected || $[25] !== request_id) {
    t14 = () => handleSelected(request_id);
    $[24] = handleSelected;
    $[25] = request_id;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t13 || $[28] !== t14) {
    t15 = <Div flex={t10} m={t11}>{t12}<Button outline={true} disabled={t13} onClick={t14} icon="check">Apply</Button></Div>;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t8) {
    t16 = <Dropdown label="Change input" expand_icon="chevron_down" icon="change" z_index={1080} m={t3} ghost={true}><Div p={4} style={t4}>{t5}{t8}{t9}{t15}</Div></Dropdown>;
    $[30] = t15;
    $[31] = t8;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
};
const ChangeDropdownExecutions: React.FC<{
  executions: TransformationExecution[];
  selected_id: string | null;
  transformation_id: string;
  setData: (id: string, data: ShortEventData, event_data_id?: string | null) => void;
}> = t0 => {
  const $ = _c(31);
  const {
    executions,
    transformation_id,
    setData
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const [t1] = useField("execution_id");
  const {
    value: execution_id
  } = t1;
  let t2;
  if ($[0] !== HookdeckAPI.transformations || $[1] !== setData || $[2] !== transformation_id) {
    t2 = id => HookdeckAPI.transformations.getExecution(transformation_id, id).then(execution => {
      setData(id, execution.original_event_data, execution.original_event_data_id);
    });
    $[0] = HookdeckAPI.transformations;
    $[1] = setData;
    $[2] = transformation_id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleSelected = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 2
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      minWidth: "592px"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Text semi={true} size="xs" muted={true} m={{
      t: 0,
      b: 1
    }}>Select from the latest executions</Text>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== execution_id || $[8] !== executions || $[9] !== handleSelected) {
    let t7;
    if ($[11] !== execution_id || $[12] !== handleSelected) {
      t7 = execution_0 => <HoverableCardSection key={execution_0.id} flex={{
        align: "center",
        gap: 3,
        justify: "space-between"
      }} p={3}><Div flex={{
          align: "center",
          gap: 3
        }}><Badge danger={execution_0.log_level === "fatal" || execution_0.log_level === "error"} warning={execution_0.log_level === "warn"} primary={execution_0.log_level === "info" || execution_0.log_level === "debug"} success={!execution_0.log_level} subtle={true}>{execution_0.log_level?.toUpperCase() || "OK"}</Badge><Text mono={true} muted={true} flex={{
            align: "center",
            gap: 1
          }}><DisplayDate key={execution_0.created_at} date={execution_0.created_at} /><span>·</span>{execution_0.id}</Text></Div><ApplyButtonWrapper><Button ghost={true} disabled={execution_id === execution_0.id} m={{
            y: -4,
            r: -1.5
          }} onClick={() => handleSelected(execution_0.id)}>Apply</Button></ApplyButtonWrapper></HoverableCardSection>;
      $[11] = execution_id;
      $[12] = handleSelected;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = executions.map(t7);
    $[7] = execution_id;
    $[8] = executions;
    $[9] = handleSelected;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <StyledCard>{t6}</StyledCard>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Div flex={{
      align: "center",
      gap: 2
    }} m={{
      y: 6
    }}><Divider /><Text semi={true} muted={true} size="xs">or</Text><Divider /></Div>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Text semi={true} size="s" m={{
      b: 1
    }}>Enter execution ID</Text>;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "center"
    };
    t11 = {
      t: 2
    };
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TextInput w={100} m={{
      b: 0
    }} name="execution_id" placeholder="Execution ID" />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = !execution_id;
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      l: 2,
      r: 0
    };
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== execution_id || $[23] !== handleSelected) {
    t15 = () => handleSelected(execution_id);
    $[22] = execution_id;
    $[23] = handleSelected;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t13 || $[26] !== t15) {
    t16 = <Div flex={t10} m={t11}>{t12}<Button ghost={true} disabled={t13} m={t14} onClick={t15}>Apply</Button></Div>;
    $[25] = t13;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t16 || $[29] !== t7) {
    t17 = <Dropdown label="Change input" icon="change" expand_icon="chevron_down" z_index={1080} ghost={true} m={t3}><Div p={4} style={t4}>{t5}{t7}{t8}{t9}{t16}</Div></Dropdown>;
    $[28] = t16;
    $[29] = t7;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  return t17;
};
export default IOPanel;
function _temp(text) {
  return [...fileContentHints(text), ...binaryBodyHints(text)];
}